import {
  UsersService,
  type CreateUserAdminRequest,
  type UpdateUserAdminRequest,
  type UserDto
} from '@/backend/market'
import { UserRepository } from '../../domain/repositories/UserRepository'
import { UserModel } from '../models/UserModel'
import type { UsersFilterEntity } from '../../domain/entities/UsersFilterEntity'
import { UsersFilterModel } from '../models/UsersFilterModel'
import { UserMiniModel } from '../models/UserMiniModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { UserEntity } from '../../domain/entities/UserEntity'
import type { UpdateUserRoleEntity } from '../../domain/entities/UpdateUserRoleEntity'
import { UpdateUserRoleModel } from '../models/UpdateUserRoleModel'
import { UserRoleModel } from '../models/UserRoleModel'
import type { UserRoleEntity } from '../../domain/entities/UserRoleEntity'
import { RoleModel } from '@/features/role/data/models/RoleModel'
import type { RoleEntity } from '@/features/role/domain/entities/RoleEntity'
import type { CreateUpdateUserEntity } from '../../domain/entities/CreateUpdateUserEntity'
import { CreateUpdateUserModel } from '../models/CreateUpdateUserModel'
import type { UserMiniEntity } from '../../domain/entities/UserMiniEntity'
import { UserOrderSummaryModel } from '../models/UserOrderSummaryModel'
import type { UserOrderSummaryEntity } from '../../domain/entities/UserOrderSummaryEntity'

export class UserRepositoryImpl implements UserRepository {
  async getUsers(
    filterData: UsersFilterEntity
  ): Promise<PaginateEntity<UserMiniModel[]> | { error: true }> {
    const usersFilterModel = new UsersFilterModel(filterData)
    try {
      const response = await UsersService.getUsersAdmin(usersFilterModel)
      const userMiniModels = response.items?.map(
        (item) => new UserMiniModel(item as UserMiniEntity)
      )
      const result = new PaginateEntity<UserMiniModel[]>(response, userMiniModels)

      return result
    } catch {
      return { error: true }
    }
  }

  async updateUser(
    userId: string,
    userData: CreateUpdateUserEntity
  ): Promise<{ isSuccess: boolean }> {
    try {
      const updateUserModel = new CreateUpdateUserModel(userData)
      await UsersService.postUserRequestUpdateAdmin({
        userId,
        requestBody: updateUserModel as unknown as UpdateUserAdminRequest
      })
      return { isSuccess: true }
    } catch {
      return { isSuccess: false }
    }
  }

  async getUserById(id: string): Promise<UserEntity | { error: true }> {
    try {
      const response = await UsersService.getUserAdmin({
        id
      })

      const userModel = new UserModel(response as UserDto)
      return userModel
    } catch {
      return { error: true }
    }
  }

  async updateUserRole(
    id: string,
    data: UpdateUserRoleEntity
  ): Promise<RoleEntity[] | { error: true }> {
    const updateUserRoleModel = new UpdateUserRoleModel(data)
    try {
      const response = await UsersService.putUserRolesAdmin({
        id,
        requestBody: updateUserRoleModel
      })
      const roleModels = response.roles?.map((role) => new RoleModel(role))
      return roleModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getUserRoles(id: string): Promise<UserRoleEntity | { error: true }> {
    try {
      const response = await UsersService.getUserRolesAdmin({
        id
      })
      const userRoleModel = new UserRoleModel(response)
      return userRoleModel
    } catch {
      return { error: true }
    }
  }

  async createUser(
    userData: CreateUpdateUserEntity
  ): Promise<{ success: true; userId: string } | { error: true }> {
    const createUpdateUserModel = new CreateUpdateUserModel(userData)
    try {
      const response = await UsersService.postUserRequestCreationAdmin({
        requestBody: createUpdateUserModel as CreateUserAdminRequest
      })
      return { success: true, userId: response.id ?? '' }
    } catch {
      return { error: true }
    }
  }
  async makeUserVip(id: string, isVip: boolean): Promise<{ success: true } | { error: true }> {
    try {
      await UsersService.putUserVipAdmin({
        id,
        requestBody: {
          vip: isVip
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getUserOrderSummery(id: string): Promise<UserOrderSummaryEntity | { error: true }> {
    try {
      const response = await UsersService.getUserOrderSummaryAdmin({
        id
      })
      const userOrderSummaryModel = new UserOrderSummaryModel(response)
      return userOrderSummaryModel
    } catch {
      return { error: true }
    }
  }
}
